/**
 * Client-side impression/click reporting for NETWORK ad zones (ad_zones rows
 * served through /api/ads/network-grid-ad), posting to /api/ads/zone-interact.
 *
 * Distinct from lib/ad-store's trackImpression/trackClick, which report house
 * campaigns (campaign_id / house_ad_id) rather than network zones.
 *
 * `zone_id` must be our ad_zones UUID — NOT the numeric ExoClick id that
 * extractExoclickZoneId() pulls out of an embed tag. The route validates the
 * UUID shape and rejects anything else, so passing the network's own id
 * silently drops the event.
 *
 * On click tracking and iframes: a js_embed zone renders the network's tag
 * inside a sandboxed cross-origin iframe, and the browser will not tell us
 * about clicks inside it (see the note in CLAUDE.md about not trying to
 * measure cross-origin ad frames). So click reporting is only honest on paths
 * where WE own the click target — a banner_image_url anchor. Impressions are
 * always knowable, because we control whether the frame was rendered at all.
 */

interface ZoneEventOptions {
  /** Our ad_zones UUID. Anything else is rejected by the route. */
  zoneId: string | null | undefined
}

function postZoneEvent(zoneId: string | null | undefined, event: "impression" | "click") {
  if (!zoneId) return
  // Fire-and-forget: ad telemetry must never block or break rendering, and a
  // failed beacon is not worth surfacing to the viewer.
  fetch("/api/ads/zone-interact", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      zone_id: zoneId,
      event,
      page_url: typeof window !== "undefined" ? window.location.href : undefined,
      device_type:
        typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop",
    }),
    keepalive: true,
  }).catch(() => {})
}

export function trackZoneImpression({ zoneId }: ZoneEventOptions) {
  postZoneEvent(zoneId, "impression")
}

export function trackZoneClick({ zoneId }: ZoneEventOptions) {
  postZoneEvent(zoneId, "click")
}
